<template>
  <div class="ai-query-page">
    <!-- Context nav bar -->
    <b-navbar type="dark" variant="primary" toggleable="md" id="context-nav" style="width: 100%;">
      <b-navbar-toggle target="nav_dropdown_collapse"></b-navbar-toggle>
      <b-collapse is-nav id="nav_dropdown_collapse">
        <b-navbar-nav>
          <b-nav-item><b-button @click="downloadCsv" :disabled="!hasResults">download csv</b-button></b-nav-item>
        </b-navbar-nav>
        <b-navbar-nav>
          <b-nav-item><b-button @click="showImportModal" :disabled="!hasResults">import as spreadsheet</b-button></b-nav-item>
          <b-nav-item>
            <modal-help topic="ai-query" label="Help for this screen" show-label />
          </b-nav-item>
        </b-navbar-nav>
      </b-collapse>
    </b-navbar>

    <div class="ai-query-body">
      <!-- Sidebar: history -->
      <div class="ai-query-sidebar">
        <div class="sidebar-header">
          <div class="sidebar-section-label">History</div>
          <button class="new-chat-btn" @click="newChat" title="New chat">+</button>
        </div>
        <div class="ai-history-list">
          <div
            v-for="entry in history"
            :key="entry._id"
            class="ai-history-item"
            :class="{ 'ai-history-item-active': activeHistoryId === entry._id }"
            @click="loadHistoryEntry(entry._id)"
          >
            <div class="ai-history-item-prompt">{{ entry.prompt }}</div>
            <div class="ai-history-item-meta">
              <span>{{ entry.turnCount > 1 ? entry.turnCount + ' turns' : entry.rowCount + ' row' + (entry.rowCount === 1 ? '' : 's') }}</span>
              <span>{{ formatDate(entry.createdAt) }}</span>
            </div>
            <button class="ai-history-item-delete" @click.stop="deleteHistoryEntry(entry._id)" title="Delete">&times;</button>
          </div>
          <div v-if="!history.length" class="ai-history-empty">No history yet</div>
        </div>
        <button
          v-if="history.length"
          class="ai-history-clear-btn"
          @click="clearHistory"
        >clear history</button>
      </div>

      <!-- Main content -->
      <div class="ai-query-main">
        <!-- Scrollable conversation thread -->
        <div class="ai-conversation-thread" ref="conversationThread">
          <!-- Empty state -->
          <div v-if="!conversation.length && !querying" class="ai-query-empty">
            Ask a question about your data and the AI will query it for you.
          </div>

          <!-- Conversation turns -->
          <div v-for="(turn, i) in conversation" :key="i" class="ai-conversation-turn">
            <!-- User prompt -->
            <div class="ai-turn-user">
              <div class="ai-turn-label">You</div>
              <div class="ai-turn-user-text">{{ turn.prompt }}</div>
            </div>

            <!-- AI response (only shown once loaded) -->
            <div v-if="turn.answer !== null" class="ai-turn-assistant">
              <div class="ai-turn-label ai-turn-label-ai">AI</div>
              <div v-if="turn.answer" class="ai-turn-answer-text">{{ turn.answer }}</div>

              <div v-if="turn.sql" class="ai-turn-sql-section">
                <div class="ai-query-sql-label">SQL Executed:</div>
                <pre class="ai-query-sql">{{ turn.sql }}</pre>
              </div>

              <div v-if="turn.results && turn.results.length" class="ai-turn-results-section">
                <div class="ai-query-results-meta">
                  {{ turn.results.length }} row{{ turn.results.length === 1 ? '' : 's' }} returned
                </div>
                <div class="ai-query-table-container">
                  <table class="ai-query-table">
                    <thead>
                      <tr>
                        <th v-for="col in turn.columns" :key="col">{{ col }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, ri) in turn.results" :key="ri">
                        <td v-for="col in turn.columns" :key="col + '-' + ri">
                          {{ row[col] != null ? row[col] : '' }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading state -->
          <div v-if="querying" class="ai-query-loading">
            <div class="ai-query-spinner"></div>
            <span>Thinking...</span>
          </div>
        </div>

        <!-- Error display -->
        <div v-if="queryError" class="ai-query-error">
          <span>{{ queryError }}</span>
          <button class="ai-query-error-dismiss" @click="queryError = null">&times;</button>
        </div>

        <!-- Prompt area (bottom) -->
        <div class="ai-query-prompt-container">
          <textarea
            v-model="prompt"
            :placeholder="conversation.length ? 'Ask a follow-up question...' : 'Ask a question about your data...'"
            rows="2"
            class="ai-query-input"
            @keydown.ctrl.enter="runQuery"
            @keydown.meta.enter="runQuery"
          ></textarea>
          <button
            class="ai-query-btn"
            @click="runQuery"
            :disabled="querying || !prompt.trim()"
          >
            {{ querying ? 'Querying...' : 'Ask' }}
          </button>
        </div>
        <div class="ai-query-usage">
          <AiTokenUsage />
        </div>
      </div>
    </div>

    <!-- Import modal -->
    <b-modal v-model="importModalVisible" title="Import results as spreadsheet" @ok="doImport" :ok-disabled="importSubmitting || !importReady" ok-title="Import">
      <template #title>
        Import results as spreadsheet
        <modal-help topic="import-query-results" />
      </template>
      <b-form-group label="Spreadsheet title">
        <b-form-input v-model="importTitle" placeholder="Query Results" @input="onImportTitleInput" />
      </b-form-group>
      <b-form-group label="Unique title">
        <b-form-input v-model="importUniqueTitle" placeholder="query_results" />
      </b-form-group>
      <div v-if="importError" class="ai-query-error" style="margin: 8px 0 0 0;">{{ importError }}</div>
      <div v-if="importSubmitting" style="color: #707070; font-size: 0.8rem; margin-top: 8px;">Importing...</div>
    </b-modal>
  </div>
</template>

<script>
import AiTokenUsage from '../AiTokenUsage.vue'

export default {
  name: 'AiQuery',
  components: { AiTokenUsage },
  data () {

    return {
      prompt: '',
      querying: false,
      queryError: null,

      conversation: [],
      sessionMessages: [],
      activeHistoryId: null,

      history: [],

      importModalVisible: false,
      importTitle: '',
      importUniqueTitle: '',
      importSubmitting: false,
      importError: null
    }
  },
  computed: {
    latestResultsTurn () {

      for (let i = this.conversation.length - 1; i >= 0; i--) {

        if (this.conversation[i].results && this.conversation[i].results.length) {

          return this.conversation[i]
        }
      }

      return null
    },
    hasResults () {

      return this.latestResultsTurn !== null
    },
    importReady () {

      return this.importTitle.trim().length > 0 && this.importUniqueTitle.trim().length > 0
    }
  },
  mounted () {

    this.loadHistory()
  },
  watch: {
    '$route.params.application' () {
      this.history = []
      this.conversation = []
      this.sessionMessages = []
      this.activeHistoryId = null
      this.loadHistory()
    }
  },
  methods: {
    async loadHistory () {

      try {

        const response = await this.$http.get(
          `${VUE_APP_PROXY_SERVER_URL}/ai-query/history`,
          { headers: { 'application-unique-title': this.$route.params.application } }
        )

        this.history = response.data
      } catch (error) {

        console.error('Failed to load history:', error)
      }
    },

    async loadHistoryEntry (id) {

      try {

        const response = await this.$http.get(
          `${VUE_APP_PROXY_SERVER_URL}/ai-query/history/${id}`,
          { headers: { 'application-unique-title': this.$route.params.application } }
        )

        const entry = response.data

        // Backwards compat: old entries without conversation array
        if (!entry.conversation) {

          entry.conversation = [{
            prompt: entry.prompt,
            answer: entry.answer,
            sql: entry.sql,
            columns: entry.columns || [],
            results: entry.results || []
          }]
        }

        this.conversation = entry.conversation
        this.activeHistoryId = id
        this.queryError = null
        this.prompt = ''

        // Rebuild session messages from conversation
        this.sessionMessages = []

        for (const turn of this.conversation) {

          this.sessionMessages.push({ role: 'user', content: turn.prompt })
          this.sessionMessages.push({ role: 'assistant', content: this._buildAssistantContent(turn) })
        }

        this.$nextTick(() => {

          if (this.$refs.conversationThread) {

            this.$refs.conversationThread.scrollTop = this.$refs.conversationThread.scrollHeight
          }
        })
      } catch (error) {

        console.error('Failed to load history entry:', error)
      }
    },

    async deleteHistoryEntry (id) {

      try {

        await this.$http.delete(
          `${VUE_APP_PROXY_SERVER_URL}/ai-query/history/${id}`,
          { headers: { 'application-unique-title': this.$route.params.application } }
        )

        this.history = this.history.filter(e => e._id !== id)

        if (this.activeHistoryId === id) {

          this.newChat()
        }
      } catch (error) {

        console.error('Failed to delete history entry:', error)
      }
    },

    async clearHistory () {

      try {

        await this.$http.delete(
          `${VUE_APP_PROXY_SERVER_URL}/ai-query/history`,
          { headers: { 'application-unique-title': this.$route.params.application } }
        )

        this.history = []
        this.newChat()
      } catch (error) {

        console.error('Failed to clear history:', error)
      }
    },

    _buildAssistantContent (turn) {

      let content = turn.answer || ''

      if (turn.sql) {

        content += `\n\nSQL executed: ${turn.sql}`
      }

      if (turn.results && turn.results.length && turn.columns && turn.columns.length) {

        content += `\n\nResults (${turn.results.length} rows):\n`
        content += turn.columns.join(' | ') + '\n'

        const preview = turn.results.slice(0, 20)

        for (const row of preview) {

          content += turn.columns.map(c => row[c] != null ? row[c] : '').join(' | ') + '\n'
        }

        if (turn.results.length > 20) {

          content += `... and ${turn.results.length - 20} more rows`
        }
      }

      return content
    },

    newChat () {

      this.conversation = []
      this.sessionMessages = []
      this.activeHistoryId = null
      this.prompt = ''
      this.queryError = null
    },

    formatDate (dateStr) {

      if (!dateStr) return ''

      const d = new Date(dateStr)
      const month = d.getMonth() + 1
      const day = d.getDate()
      const hours = d.getHours()
      const mins = String(d.getMinutes()).padStart(2, '0')

      return `${month}/${day} ${hours}:${mins}`
    },

    async runQuery () {

      if (!this.prompt.trim() || this.querying) return

      this.querying = true
      this.queryError = null

      const currentPrompt = this.prompt.trim()
      this.prompt = ''

      // Show user message immediately
      this.conversation.push({
        prompt: currentPrompt,
        answer: null,
        sql: null,
        columns: [],
        results: []
      })

      this.$nextTick(() => {

        if (this.$refs.conversationThread) {

          this.$refs.conversationThread.scrollTop = this.$refs.conversationThread.scrollHeight
        }
      })

      try {

        const response = await this.$http.post(
          `${VUE_APP_PROXY_SERVER_URL}/ai-query`,
          {
            prompt: currentPrompt,
            messages: this.sessionMessages,
            historyId: this.activeHistoryId
          },
          {
            headers: { 'application-unique-title': this.$route.params.application },
            timeout: 600000
          }
        )

        // Update the pending turn with the response
        const lastTurn = this.conversation[this.conversation.length - 1]
        lastTurn.answer = response.data.answer
        lastTurn.sql = response.data.sql
        lastTurn.columns = response.data.columns || []
        lastTurn.results = response.data.results || []

        // Update session messages for next turn's LLM context
        this.sessionMessages.push({ role: 'user', content: currentPrompt })
        this.sessionMessages.push({ role: 'assistant', content: this._buildAssistantContent(lastTurn) })

        this.activeHistoryId = response.data._id || null

        await this.loadHistory()
      } catch (error) {

        // Remove the pending turn on error
        this.conversation.pop()
        this.queryError = error.response ? (error.response.data || error.message) : error.message
      }

      this.querying = false

      this.$nextTick(() => {

        if (this.$refs.conversationThread) {

          this.$refs.conversationThread.scrollTop = this.$refs.conversationThread.scrollHeight
        }
      })
    },

    downloadCsv () {

      const latest = this.latestResultsTurn

      if (!latest) return

      const columns = latest.columns
      const results = latest.results
      const header = columns.join(',')

      const rows = results.map(row =>
        columns.map(col => {
          const val = row[col] != null ? String(row[col]) : ''

          if (val.includes(',') || val.includes('"') || val.includes('\n')) {

            return '"' + val.replace(/"/g, '""') + '"'
          }

          return val
        }).join(',')
      )

      const csvContent = [header, ...rows].join('\n')
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = 'query_results.csv'
      link.click()
      URL.revokeObjectURL(link.href)
    },

    showImportModal () {

      this.importTitle = 'Query Results'
      this.importUniqueTitle = 'query_results_' + Date.now()
      this.importError = null
      this.importSubmitting = false
      this.importModalVisible = true
    },

    onImportTitleInput () {

      this.importUniqueTitle = this.importTitle
        .toLowerCase()
        .replace(/[^a-z0-9_\s-]/g, '')
        .replace(/[\s-]+/g, '_')
        .replace(/^_+|_+$/g, '')
    },

    async doImport (event) {

      if (!this.importReady) {

        event.preventDefault()
        return
      }

      event.preventDefault()
      this.importSubmitting = true
      this.importError = null

      const latest = this.latestResultsTurn

      if (!latest) {

        this.importError = 'No results to import'
        this.importSubmitting = false
        return
      }

      const columns = latest.columns
      const results = latest.results

      try {

        const appHeader = { 'application-unique-title': this.$route.params.application }

        // Step 1: Create spreadsheet
        await this.$http.post(
          `${VUE_APP_PROXY_SERVER_URL}/spreadsheets/`,
          { title: this.importTitle, uniqueTitle: this.importUniqueTitle },
          { headers: { 'Content-Type': 'application/json', ...appHeader } }
        )

        // Step 2: Create columns
        for (const colName of columns) {

          const colType = this._inferColumnType(colName, results)

          await this.$http.post(
            `${VUE_APP_PROXY_SERVER_URL}/spreadsheets/${this.importUniqueTitle}/columns/`,
            { title: colName, uniqueTitle: colName, type: colType },
            { headers: appHeader }
          )
        }

        // Step 3: Bulk import rows
        const rowsToImport = results.map(row => {

          const obj = {}

          for (const col of columns) {

            obj[col] = row[col] != null ? row[col] : null
          }

          return obj
        })

        await this.$http.post(
          `${VUE_APP_PROXY_SERVER_URL}/data/${this.importUniqueTitle}/bulk`,
          { rows: rowsToImport },
          { headers: appHeader }
        )

        this.importModalVisible = false

        // Navigate to the spreadsheets view showing the new sheet
        this.$router.push(`/${this.$route.params.application}/spreadsheets/${this.importUniqueTitle}`)
      } catch (error) {

        const msg = error?.response?.data || error?.message || 'Import failed'
        this.importError = typeof msg === 'string' ? msg : JSON.stringify(msg)
        event.preventDefault()
      }

      this.importSubmitting = false
    },

    _inferColumnType (colName, results) {

      const samples = results.slice(0, 20)
        .map(r => r[colName])
        .filter(v => v != null && v !== '')

      if (samples.length === 0) return 'text'

      const allNumbers = samples.every(v => !isNaN(Number(v)) && String(v).trim() !== '')

      if (allNumbers) return 'number'

      const isoDate = /^\d{4}-\d{2}-\d{2}/

      const allDates = samples.every(v => {

        const s = String(v).trim()

        return isoDate.test(s) || (!isNaN(Date.parse(s)) && s.length > 6)
      })

      if (allDates) return 'dateTime'

      return 'text'
    }
  }
}
</script>

<style scoped>

.ai-query-page {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ai-query-body {
  flex: 1;
  display: flex;
  overflow: hidden;
  min-height: 0;
}

/* ── Sidebar ── */
.ai-query-sidebar {
  width: 25%;
  min-width: 200px;
  max-width: 320px;
  border-right: 1px dotted #474842;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 10px;
  flex-shrink: 0;
}

.sidebar-section-label {
  padding: 10px 12px 6px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #a8a8a4;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.new-chat-btn {
  background: none;
  border: 1px solid #333330;
  border-radius: 4px;
  color: #707070;
  font-size: 1rem;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: background 0.1s, color 0.1s, border-color 0.1s;
}

.new-chat-btn:hover {
  background: #222220;
  color: #9ad12b;
  border-color: #484845;
}

.ai-history-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 4px;
}

.ai-history-item {
  padding: 8px 10px;
  margin: 2px 0;
  border-radius: 4px;
  cursor: pointer;
  position: relative;
  transition: background 0.1s;
}

.ai-history-item:hover {
  background: #1e1e1b;
}

.ai-history-item-active {
  background: #1a1a17 !important;
  border-left: 2px solid #9ad12b;
}

.ai-history-item-prompt {
  font-size: 0.8rem;
  color: #c0c0be;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: 18px;
}

.ai-history-item-active .ai-history-item-prompt {
  color: #e8e8e6;
}

.ai-history-item-meta {
  display: flex;
  justify-content: space-between;
  font-size: 0.68rem;
  color: #a8a8a4;
  margin-top: 3px;
}

.ai-history-item-delete {
  position: absolute;
  top: 6px;
  right: 6px;
  background: none;
  border: none;
  color: #a8a8a4;
  font-size: 1rem;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
  opacity: 0;
  transition: opacity 0.1s, color 0.1s;
}

.ai-history-item:hover .ai-history-item-delete {
  opacity: 1;
}

.ai-history-item-delete:hover {
  color: #f82471;
}

.ai-history-empty {
  padding: 20px 12px;
  text-align: center;
  color: #3e3e3c;
  font-size: 0.78rem;
}

.ai-history-clear-btn {
  flex-shrink: 0;
  margin: 4px 8px 8px;
  padding: 4px 10px;
  background: transparent;
  border: 1px solid #333330;
  border-radius: 4px;
  color: #707070;
  font-size: 0.72rem;
  cursor: pointer;
  transition: background 0.1s, color 0.1s, border-color 0.1s;
}

.ai-history-clear-btn:hover {
  background: #222220;
  color: #c0c0be;
  border-color: #484845;
}

/* ── Main content ── */
.ai-query-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
}

/* ── Conversation thread ── */
.ai-conversation-thread {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  min-height: 0;
}

.ai-conversation-turn {
  margin-bottom: 20px;
}

.ai-conversation-turn:last-child {
  margin-bottom: 8px;
}

.ai-turn-user {
  margin-bottom: 10px;
}

.ai-turn-label {
  font-size: 0.68rem;
  font-weight: 600;
  color: #707070;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
}

.ai-turn-label-ai {
  color: #9ad12b;
}

.ai-turn-user-text {
  background: #1e1e1b;
  border: 1px solid #2a2a27;
  border-radius: 6px;
  padding: 8px 12px;
  color: #e8e8e6;
  font-size: 0.85rem;
  white-space: pre-wrap;
}

.ai-turn-assistant {
  margin-left: 0;
}

.ai-turn-answer-text {
  color: #c0c0be;
  font-size: 0.85rem;
  line-height: 1.5;
  white-space: pre-wrap;
  margin-bottom: 8px;
}

.ai-turn-sql-section {
  margin-bottom: 8px;
}

.ai-turn-results-section {
  margin-bottom: 4px;
}

/* ── Prompt area (bottom) ── */
.ai-query-usage {
  padding: 0 16px 10px;
}

.ai-query-prompt-container {
  display: flex;
  gap: 10px;
  padding: 12px 16px;
  align-items: flex-start;
  flex-shrink: 0;
  border-top: 1px solid #2a2a27;
}

.ai-query-input {
  flex: 1;
  background-color: #1e1e1b;
  color: #e8e8e6;
  border: 1px solid #2a2a27;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 0.85rem;
  font-family: inherit;
  resize: vertical;
  outline: none;
  transition: border-color 0.15s;
}

.ai-query-input:focus {
  border-color: #474842;
}

.ai-query-input::placeholder {
  color: #a8a8a4;
}

.ai-query-btn {
  background-color: #9ad12b;
  border: none;
  border-radius: 4px;
  color: #0f0f0d;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 8px 20px;
  cursor: pointer;
  transition: background 0.12s, opacity 0.12s;
  flex-shrink: 0;
}

.ai-query-btn:hover {
  background-color: #aee033;
}

.ai-query-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── Shared styles ── */
.ai-query-sql-label {
  font-size: 0.72rem;
  color: #707070;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.ai-query-sql {
  margin: 4px 0 0;
  padding: 8px 12px;
  background: #141412;
  border: 1px solid #2a2a27;
  border-radius: 4px;
  color: #9ad12b;
  font-size: 0.8rem;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

.ai-query-error {
  margin: 0 16px 8px;
  padding: 8px 12px;
  background: #2a1015;
  border: 1px solid #5a2030;
  border-radius: 4px;
  color: #f82471;
  font-size: 0.82rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.ai-query-error-dismiss {
  background: none;
  border: none;
  color: #f82471;
  font-size: 1.1rem;
  cursor: pointer;
  padding: 0 4px;
  opacity: 0.7;
}

.ai-query-error-dismiss:hover {
  opacity: 1;
}

.ai-query-results-meta {
  padding: 4px 0;
  font-size: 0.72rem;
  color: #707070;
}

.ai-query-table-container {
  overflow: auto;
  border: 1px solid #2a2a27;
  border-radius: 4px;
  max-height: 300px;
}

.ai-query-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
}

.ai-query-table th {
  background: #141412;
  color: #9ad12b;
  padding: 6px 10px;
  border-bottom: 1px solid #2a2a27;
  position: sticky;
  top: 0;
  text-align: left;
  font-weight: 600;
  white-space: nowrap;
}

.ai-query-table td {
  padding: 5px 10px;
  border-bottom: 1px solid #1e1e1b;
  color: #e8e8e6;
  white-space: nowrap;
}

.ai-query-table tr:hover td {
  background: #1a1a17;
}

.ai-query-empty {
  padding: 80px 16px;
  text-align: center;
  color: #3e3e3c;
  font-size: 0.9rem;
}

.ai-query-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 0;
  color: #707070;
  font-size: 0.85rem;
}

.ai-query-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid #2a2a27;
  border-top-color: #9ad12b;
  border-radius: 50%;
  animation: ai-spin 0.7s linear infinite;
}

@keyframes ai-spin {
  to { transform: rotate(360deg); }
}
</style>
